import { useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import { useAppSelector } from '../../hooks';
import { RootState } from '../../store';
import { checkPermission } from '@pcmp-checka/lib-utilities/atkomst';
import { useLocationsQuery } from '@/redux/back-dashboard-api';
import { useGetAppConfigQuery } from '@/redux/back-dashboard-api';
import { QUERY_LIMIT } from '@/lib/constants';

export const useSession = () =>
  useAppSelector((state: RootState) => state.sessionSlice);

const selectUser = createSelector(
  (state: RootState) => state.sessionSlice.session?.user,
  (state: RootState) => state.sessionSlice.profile,
  (state: RootState) => state.sessionSlice.isManager,
  (user, profile, isManager) => ({
    'https://accounts.ikea.com/email':
      user?.['https://accounts.ikea.com/email'] || '',
    'https://accounts.ikea.com/upn':
      user?.['https://accounts.ikea.com/upn'] || '',
    'https://accounts.ikea.com/guid':
      user?.['https://accounts.ikea.com/guid'] || '',
    'https://accounts.ikea.com/networkid':
      user?.['https://accounts.ikea.com/networkid'] || '',
    given_name: user?.given_name || '',
    family_name: user?.family_name || '',
    nickname: user?.nickname || '',
    name: user?.name || user?.email || '',
    picture: user?.picture || '',
    updated_at: user?.updated_at || '',
    email: user?.email || '',
    email_verified: user?.email_verified || false,
    sub: user?.sub || '',
    sid: user?.sid || '',
    manager: user?.manager || { displayName: '', id: '', mail: '' },
    ...(profile || {}),
    isManager,
  })
);

export const useUser = () => useAppSelector(selectUser);

export const useUserRoles = () => {
  const { roles } = useSession();
  const { data: appConfig } = useGetAppConfigQuery(undefined);
  const viewerMode =
    appConfig?.appConfig?.maintenanceStatus?.enabled &&
    appConfig?.appConfig?.maintenanceStatus?.forceViewerMode;

  return useMemo(() => {
    return viewerMode ? [] : roles || [];
  }, [roles, viewerMode]);
};

export const useUserLocations = () => {
  const roles = useUserRoles();
  const { data, isLoading, error } = useLocationsQuery({
    pagination: { limit: QUERY_LIMIT },
  });
  const locations = useMemo(
    () =>
      data?.locations.nodes.filter((loc) =>
        checkPermission(roles, 'order.create', loc.atkomstClientId, loc.country)
      ),
    [data, roles]
  );

  return useMemo(
    () => ({
      data: locations,
      isLoading,
      error,
    }),
    [locations, isLoading, error]
  );
};
